/**
 * MeetingsCalendar Page
 * Full-page calendar view for all customer meetings
 */

import { addMonths, endOfMonth, format, startOfMonth, subMonths } from "date-fns";
import { ArrowLeft, CalendarDays, Plus } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { EditMeetingDialog } from "@/components/meetings/EditMeetingDialog";
import { MeetingCalendar } from "@/components/meetings/MeetingCalendar";
import { ScheduleMeetingDialog } from "@/components/meetings/ScheduleMeetingDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCalendarMeetings } from "@/hooks/useCustomerMeetings";
import type { MeetingStatus, MeetingWithCustomer } from "@/types/meeting.types";

export function MeetingsCalendar() {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [statusFilter, setStatusFilter] = useState<MeetingStatus | "all">("all");
  const [selectedMeeting, setSelectedMeeting] = useState<MeetingWithCustomer | null>(null);
  const [isScheduling, setIsScheduling] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Calculate date range - fetch 2 months before and after for smooth navigation
  const dateFrom = format(subMonths(startOfMonth(currentDate), 2), "yyyy-MM-dd");
  const dateTo = format(addMonths(endOfMonth(currentDate), 2), "yyyy-MM-dd");

  // Fetch meetings for calendar view
  const { data: meetings = [], isLoading } = useCalendarMeetings(dateFrom, dateTo);

  // Filter meetings by status
  const filteredMeetings =
    statusFilter === "all" ? meetings : meetings.filter((m) => m.status === statusFilter);

  const handleSelectEvent = (meeting: MeetingWithCustomer) => {
    setSelectedMeeting(meeting);
  };

  const handleSelectSlot = (slotInfo: { start: Date; end: Date }) => {
    // Open schedule meeting dialog with clicked date pre-filled
    setSelectedDate(slotInfo.start);
    setIsScheduling(true);
  };

  const handleDateChange = (newDate: Date) => {
    setCurrentDate(newDate);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        onNewProject={() => navigate("/new-project")}
        onViewProjects={() => navigate("/projects")}
      />
      <div className="container mx-auto py-6 space-y-6">
        {/* Page Header */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <CardTitle className="flex items-center gap-2">
                  <CalendarDays className="h-6 w-6" />
                  Calendar
                </CardTitle>
              </div>
              <Button onClick={() => setIsScheduling(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Schedule Event
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Filter:</span>
                <Select
                  value={statusFilter}
                  onValueChange={(v) => setStatusFilter(v as MeetingStatus | "all")}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Meetings</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="rescheduled">Rescheduled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="text-sm text-muted-foreground">
                Showing {filteredMeetings.length} meeting{filteredMeetings.length !== 1 ? "s" : ""}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Calendar */}
        <MeetingCalendar
          meetings={filteredMeetings}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
          onDateChange={handleDateChange}
          currentDate={currentDate}
          isLoading={isLoading}
        />

        {/* Schedule Meeting Dialog */}
        <ScheduleMeetingDialog
          isOpen={isScheduling}
          onClose={() => {
            setIsScheduling(false);
            setSelectedDate(null);
          }}
          customerId={null}
          initialDate={selectedDate}
        />

        {/* Meeting Detail Dialog */}
        <EditMeetingDialog
          isOpen={!!selectedMeeting}
          onClose={() => setSelectedMeeting(null)}
          meeting={selectedMeeting}
        />
      </div>
    </div>
  );
}
